import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActionArea,
  Grid,
  Pagination,
  CircularProgress,
  Alert,
  Chip,
  Stack,
  Link,
  Skeleton,
  Container,
  Paper,
  Button,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import ArticleIcon from '@mui/icons-material/Article';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PublishIcon from '@mui/icons-material/Publish';
import PageHeader from './PageHeader';
import RsocNavHeader from './RsocNavHeader';
import Api from '../../Services/Api';

const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  transition: 'all 0.2s ease-in-out',
  borderRadius: theme.spacing(1),
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[4],
  },
}));

const ArticleTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.1rem',
  fontWeight: 600,
  lineHeight: 1.3,
  marginBottom: theme.spacing(1),
  color: theme.palette.text.primary,
  display: '-webkit-box',
  '-webkit-line-clamp': 2,
  '-webkit-box-orient': 'vertical',
  overflow: 'hidden',
}));

const ArticleExcerpt = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: '0.875rem',
  lineHeight: 1.5,
  display: '-webkit-box',
  '-webkit-line-clamp': 3,
  '-webkit-box-orient': 'vertical',
  overflow: 'hidden',
  marginBottom: theme.spacing(2),
}));


const RecommendedArticles = () => {
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalArticles, setTotalArticles] = useState(0);
  
  
  const articlesPerPage = 12;

  // Fetch articles from API
  const fetchArticles = async (currentPage = 1) => {
    try {
      setLoading(true);
      setError(null);

      const response = await Api.getRecommendedArticles({
        page: currentPage,
        per_page: articlesPerPage
      });

      if (response && response.success && response.articles) {
        setArticles(response.articles);
        setTotalPages(Math.ceil((response.total || 0) / articlesPerPage));
        setTotalArticles(response.total || 0);
      } else {
        throw new Error(response?.message || 'No articles available');
      }
    } catch (err) {
      console.error('Error fetching recommended articles:', err);
      setError(err.response?.data?.message || err.message || 'Unable to fetch recommended articles. Please try again later.');
      setArticles([]);
      setTotalPages(1);
      setTotalArticles(0);
    } finally {
      setLoading(false);
    }
  };


  // Handle page change
  const handlePageChange = (event, newPage) => {
    setPage(newPage);
    fetchArticles(newPage);
    
    // Scroll to top when changing pages
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Open article in new tab
  const handleArticleClick = (article) => {
    window.open(article.url, '_blank', 'noopener,noreferrer');
  };

  // Handle publish button click
  const handlePublishClick = async (event, article) => {
    event.stopPropagation(); // Prevent card click
    
    try {
      // Fetch the template data using the article ID
      const response = await Api.getRsocTemplate(article.id);
      
      // Fetch domains to get the first one
      const domainsResponse = await Api.searchDomains();
      const firstDomain = domainsResponse?.data?.[0];
      
      if (!firstDomain) {
        setError('No domains available for publishing');
        return;
      }
      
      // Extract keywords from the template data (limit to 5 items max)
      const keywordsArray = response.keywords ? 
        response.keywords.split(',').map(k => k.trim()).slice(0, 5) : [];
      
      // Navigate to generator with pre-filled data
      navigate('/rsoc/generator', {
        state: {
          prefillData: {
            domainId: firstDomain.domain_name_id,
            categoryName: response.category, // Pass category name for matching
            title: response.title,
            keywords: keywordsArray,
            generatedContent: {
              intro: response.intro,
              contentBlocks: response.content || []
            }
          }
        }
      });
      
    } catch (err) {
      console.error('Error fetching template data:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to load article template data';
      setError(errorMessage);
    }
  };


  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return 'N/A';
    }
    
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Load articles on component mount
  useEffect(() => {
    fetchArticles(1);
  }, []);

  // Skeleton loader component
  const ArticleSkeleton = () => (
    <Card elevation={2} sx={{ borderRadius: 1 }}>
      <CardContent>
        <Skeleton variant="text" width="60%" height={24} />
        <Skeleton variant="text" height={20} sx={{ mt: 1 }} />
        <Skeleton variant="text" height={20} />
        <Skeleton variant="text" width="80%" height={20} />
        <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
          <Skeleton variant="rectangular" width={60} height={24} />
          <Skeleton variant="rectangular" width={80} height={24} />
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Container maxWidth="xl">
      <PageHeader 
        title="Recommended Articles"
        subtitle="Stay up-to-date with the latest strategies, insights, and best practices from ads.com. Discover articles curated specifically for maximizing your traffic monetization and campaign performance."
        buttonIcon={<ArticleIcon />}
      />
      
      <RsocNavHeader />
      
      <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 1 }}>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {!loading && !error && articles.length === 0 && (
          <Alert severity="info" sx={{ mb: 3 }}>
            No recommended articles available at this time. Please check back later.
          </Alert>
        )}

        {!error && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
            <Typography variant="body2" color="text.secondary">
              {loading ? 'Loading articles...' : 
               articles.length === 0 ? 'No articles found' :
               `Showing ${articles.length} of ${totalArticles} articles`}
            </Typography>
          </Box>
        )}

        {/* Articles Grid */}
        <Grid container spacing={3}>
        {loading ? (
          // Loading skeletons
          Array.from({ length: articlesPerPage }).map((_, index) => (
            <Grid item xs={12} sm={6} lg={4} key={index}>
              <ArticleSkeleton />
            </Grid>
          ))
        ) : (
          // Actual articles
          articles.map((article) => (
            <Grid item xs={12} sm={6} lg={4} key={article.id}>
              <StyledCard elevation={2}>
                <CardActionArea onClick={() => handleArticleClick(article)}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Chip 
                        label={article.category || 'Article'} 
                        size="small" 
                        color="primary" 
                        variant="outlined"
                      />
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<PublishIcon />}
                          onClick={(e) => handlePublishClick(e, article)}
                          sx={{ minWidth: 'auto' }}
                        >
                          Publish
                        </Button>
                        <OpenInNewIcon fontSize="small" color="action" />
                      </Box>
                    </Box>
                    
                    <ArticleTitle>
                      {article.title}
                    </ArticleTitle>
                    
                    <ArticleExcerpt>
                      {article.excerpt}
                    </ArticleExcerpt>
                    
                    <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                      {(article.tags || []).slice(0, 3).map((tag, index) => (
                        <Chip
                          key={index}
                          label={tag}
                          size="small"
                          variant="outlined"
                          color="default"
                          sx={{ fontSize: '0.75rem', height: 20 }}
                        />
                      ))}
                    </Stack>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 'auto' }}>
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(article.publishDate || article.published_at || article.created_at || article.date)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {article.readTime || article.read_time ? `${article.readTime || article.read_time} min read` : 'Quick read'}
                      </Typography>
                    </Box>
                  </CardContent>
                </CardActionArea>
              </StyledCard>
            </Grid>
          ))
        )}
        </Grid>

        {/* Pagination */}
        {!loading && totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={handlePageChange}
              color="primary"
              size="large"
              showFirstButton
              showLastButton
            />
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default RecommendedArticles;